import { useState, useEffect } from "react";
import useOccurrence from "../../hooks/useOccurrence";
import { LoadMoreButton } from "@rio-cloud/rio-uikit";
import { Charts } from "./components/charts";
import FilterRow from "./components/filterRow";
import CardView from "./components/cardView";
import EmptyData from "./components/emptyData";
import { OccurrenceDispatch, OccurrenceStep, OccurrenceWithTimeSpent } from "../../models/occurrence";
import { Scheduling } from "../../models/schedule";
import { Occurrence } from "../../models/occurrence";
import { OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../enums/occurrenceStep";
import TableKanbanView from "./components/tableView";
import usePartner from "../../hooks/usePartner";
import { useOccurrenceExport } from "../../hooks/useOccurrenceExport";
import ExportProgressKanbanDialog from "../../components/exportProgressKanbanDialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import { buildExportFilters } from "../../utils/kanbanUtils";
import CopyTokenButton from "./components/copyTokenButton";

import "./styles.scss";

export interface IForm {
  occurrence: Occurrence | null;
  summaryStep?: OccurrenceStep | null;
  step?: OccurrenceStep | null;
  partners?: { id: string; name: string }[];
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  loading: boolean;
  save: (occurrence: Occurrence) => Promise<boolean>;
  create?: (occurrence: Occurrence) => Promise<{ wasCreated: boolean; error?: string }>;
  onClose: () => void;
  disabledAllFields?: boolean;
  setOccurrence?: (updatedOccurrence: Partial<Occurrence>) => void;
  setOccurrenceValue: (attr: string, value: any) => void;
  setOccurrenceStepValue: (step: OccurrenceStepLabelEnum, attr: string, value: any) => void;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  addDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  cancelDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string) => Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  hiddenSaveButton?: boolean;
  changeStep?: (occurrence: Occurrence, fromStepId: number, to: OccurrenceStepEnum) => Promise<Occurrence | null>;
  getNotFilledRequiredFields: (requiredFields: string[], occurrence: Occurrence) => string[];
  finishOccurrence?: (
    occurrence: Occurrence,
    finalizationReasonType?: string,
    finalizationReasonDescription?: string,
    fromStepId?: number,
  ) => Promise<boolean>;
  scheduling?: Scheduling | null;
}

export interface IChildForm {
  partners?: { id: string; name: string }[];
  hiddenFields: string[];
  disabledFields: string[];
  disabledAllFields?: boolean;
  occurrence: Occurrence | null;
  setOccurrenceValue: (attr: string, value: any) => void;
  setOccurrence?: (updatedOccurrence: Partial<Occurrence>) => void;
  requiredFields: string[];
  step?: OccurrenceStep | null;
  setOccurrenceStepValue: (step: OccurrenceStepLabelEnum, attr: string, value: any) => void;
  hasError?: boolean;
  addDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  cancelDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string) => Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  persona?: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  setServiceBayScheduleWasUpdated?: (wasUpdated: boolean) => void;
  scheduling?: Scheduling | null;
}

export interface IKanbanPageProps {
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  assetIds?: string[];
  chassisList?: string[];
  isSidebarOpen?: boolean;
  onOccurrenceListsReady?: (current: OccurrenceWithTimeSpent[], finalized: OccurrenceWithTimeSpent[]) => void;
}

const KanbanPage = (props: IKanbanPageProps) => {
  const {
    viewType,
    setViewType,
    searchByChassis,
    chassisFilter,
    setChassisListFilter,
    setCriticalityFilter,
    setStartDateFilter,
    setUpdateDateFilter,
    setTypeFilter,
    setIsAlertFilter,
    typeFilter,
    occurrenceList,
    isValidStep,
    firstLoad,
    loading,
    setLoading,
    create,
    save,
    changeStep,
    getNotFilledRequiredFields,
    finish,
    loadingMoreData,
    getOccurrenceList,
    refreshCurrentData,
    getReview,
    review,
    reviewStep,
    occurrenceStepAvailable,
    addDispatch,
    cancelDispatch,
    finishedFilter,
    setFinishedFilter,
    setDealershipFilter,
    regionalFilter,
    setRegionalFilter,
    cellFilter,
    setCellFilter,
    setCpvNameFilter,
    setStepFilter,
    occurrenceWithTimeSpentList,
    finalizedOccurrenceWithTimeSpentList,
    rawCurrentOccurrenceWithTimeSpentList,
    totalOccurrences,
    totalInProgress,
    stepFilter,
    steps,
    criticalityFilter,
    startDateFilter,
    updateDateFilter,
    dealershipFilter,
    chassisListFilter,
    chassisListFilterByStep,
    setChassisListFilterByStep,
    setStepTypeCurrentFilter,
  } = useOccurrence(
    props.persona,
    props.finalized,
    props.assetIds,
    props.chassisList
  );

  const { partners } = usePartner();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const sidebarIsOpen = props.isSidebarOpen ?? false;

  const [isCurrentStageMode, setIsCurrentStageMode] = useState<boolean>(false);
  const [currentStepFilter, setCurrentStepFilter] = useState<OccurrenceStepEnum | null>(null);
  const [currentStageBaseList, setCurrentStageBaseList] = useState<typeof occurrenceWithTimeSpentList>([]); 
  const [updatedFilters, setUpdatedFilters] = useState<number>(0);

  useEffect(() => {
    if (props.onOccurrenceListsReady) {
      props.onOccurrenceListsReady(rawCurrentOccurrenceWithTimeSpentList, finalizedOccurrenceWithTimeSpentList);
    }
  }, [rawCurrentOccurrenceWithTimeSpentList, finalizedOccurrenceWithTimeSpentList]);

  useEffect(() => {
    if (isCurrentStageMode && currentStepFilter === null) {
      setCurrentStageBaseList(occurrenceWithTimeSpentList);
    }
  }, [isCurrentStageMode, currentStepFilter, occurrenceWithTimeSpentList]);
  useEffect(() => {
    setUpdatedFilters(prev => prev + 1);
  }, [criticalityFilter, startDateFilter, updateDateFilter, dealershipFilter, regionalFilter, cellFilter, finishedFilter, chassisListFilter]);

  const {
    open: exportOpen,
    stage: exportStage,
    progress: exportProgress,
    detail: exportDetail,
    errorMessage: exportError,
    close: closeExport,
    runAll: runExportAll,
    exportWithFilters,
  } = useOccurrenceExport();

  const handleExportAll = async () => {
    // Constrói os filtros atuais para a exportação
    const exportFilters = buildExportFilters({
      criticalityFilter,
      typeFilter,
      startDateFilter,
      updateDateFilter,
      dealershipFilter,
      regionalFilter,
      cellFilter,
      stepFilter,
      finishedFilter,
    }, [...chassisListFilter, chassisFilter].filter(Boolean));

    await exportWithFilters({
      filters: exportFilters,
      filename: `kanban_${new Date().toISOString().slice(0, 10)}.xlsx`,
      userRole: props.persona,
    });
  };

  const stageForDialog = (exportStage as unknown) === "paging" ? ("writing" as any) : exportStage;

  const getOccurrenceCurrentStep = (o: Occurrence): string | undefined => {
    if (o.stepTypeCurrent) return o.stepTypeCurrent;
    return (o.steps ?? [])
      .filter(s => s.step && s.dtStart && !s.dtEnd)
      .sort((a, b) => (b.dtStart?.getTime() ?? 0) - (a.dtStart?.getTime() ?? 0))[0]?.step;
  };

  const displayedOccurrenceList = occurrenceList;

  return (
    <>
      <Charts
        totalOccurrences={totalOccurrences}
        totalInProgress={totalInProgress}
        occurrenceStepAvailable={occurrenceStepAvailable}
        setChassisListFilter={setChassisListFilter}
        loading={loading}
        assetIds={props.assetIds}
        setStepFilter={(stepFilter) => {
          setLoading(true);
          setStepFilter(stepFilter);
          setLoading(false);
        }}
        stepFilter={stepFilter}
        occurrenceWithTimeSpentList={occurrenceWithTimeSpentList}
        finalizedOccurrenceWithTimeSpentList={finalizedOccurrenceWithTimeSpentList}
        firstLoad={firstLoad}
        showFinalized={finishedFilter.length === 0 || finishedFilter.includes('true')}
        chassisListFilterByStep={chassisListFilterByStep}
        setChassisListFilterByStep={setChassisListFilterByStep}
        finishedFilter={finishedFilter}
        setFinishedFilter={setFinishedFilter}
        updatedFilters={updatedFilters}
        isCurrentStageMode={isCurrentStageMode}
        setIsCurrentStageMode={(v) => {
          setIsCurrentStageMode(v);
          if (!v) {
            setCurrentStepFilter(null);
            setStepTypeCurrentFilter(null);
            setCurrentStageBaseList([]);
          }
        }}
        currentStepFilter={currentStepFilter}
        setCurrentStepFilter={step => {
          setCurrentStepFilter(step);
          setStepTypeCurrentFilter(step);
        }}
        currentStageBaseList={currentStageBaseList}
      />
      <div
        className={`flex items-center justify-between gap-4 mt-4 relative ${isMobile ? "flex-wrap" : ""}`}
      >
        <FilterRow
          searchByChassis={searchByChassis}
          setCriticalityFilter={setCriticalityFilter}
          setStartDateFilter={({ startDate, endDate }) =>
            setStartDateFilter({ start: startDate, end: endDate })
          }
          setUpdateDateFilter={({ startDate, endDate }) =>
            setUpdateDateFilter({ start: startDate, end: endDate })
          }
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          setIsAlertFilter={setIsAlertFilter}
          setDealershipFilter={setDealershipFilter}
          setRegionalFilter={setRegionalFilter}
          setCellFilter={setCellFilter}
          setCpvFilter={setCpvNameFilter}
          persona={props.persona}
          viewType={viewType}
          setViewType={setViewType}
          create={create}
          partners={partners}
          isValidStep={isValidStep}
          copyTokenButton={<CopyTokenButton />}
          exportButton={
            <button
              className="btn btn-default"
              onClick={handleExportAll}
            >
              <span className="rioglyph rioglyph-download !m-[0]"></span>
            </button>
          }
        />
      </div>

      {!loading && occurrenceList.length === 0 ? (
        <EmptyData />
      ) : (
        <>
          {viewType === "card" && (
            <CardView
              occurrenceList={displayedOccurrenceList}
              occurrenceWithTimeSpentList={occurrenceWithTimeSpentList}
              partners={partners}
              isValidStep={isValidStep}
              loading={loading}
              save={save}
              changeStep={changeStep}
              getNotFilledRequiredFields={getNotFilledRequiredFields}
              finishOccurrence={finish}
              disabledAllFields={props.persona === "MANAGER" || props.finalized}
              persona={props.persona}
              finalized={props.finalized}
              review={review}
              reviewStep={reviewStep}
              getReview={getReview}
              occurrenceStepAvailable={occurrenceStepAvailable}
              addDispatch={addDispatch}
              cancelDispatch={cancelDispatch}
              steps={steps}
              isSidebarOpen={sidebarIsOpen}
              onRefresh={refreshCurrentData}
            />
          )}

          {viewType === "list" && (
            <TableKanbanView
              occurrenceList={displayedOccurrenceList}
              loading={loading}
              occurrenceStepAvailable={occurrenceStepAvailable}
            />
          )}

          {!loading && !loadingMoreData && occurrenceList.length > 0 ? (
            <div className="flex justify-center padding-top-20">
              <LoadMoreButton
                loaded={displayedOccurrenceList.length}
                total={totalOccurrences}
                onLoadMore={() => getOccurrenceList({ localStepTypeCurrentFilter: isCurrentStageMode ? currentStepFilter : undefined })}
                loadMoreMessage="Carregar mais"
                noMoreMessage="Tudo carregado"
              />
            </div>
          ) : null}

          {loadingMoreData && (
            <div className="flex justify-center pt-[50px] text-color-primary">
              <div className="spinner">
                <div />
                <div />
                <div />
                <div />
              </div>
              <div className="spinnerText ml-4">Carregando mais dados...</div>
            </div>
          )}

          <ExportProgressKanbanDialog
            show={exportOpen}
            stage={stageForDialog as any}
            progress={exportProgress}
            detail={exportDetail}
            onClose={closeExport}
            errorMessage={exportError}
          />
        </>
      )}
    </>
  );
};

export default KanbanPage;
